import { useCallback, useState } from "react";

/**
 * localStorage keys for one-time dismissals. Shared so multiple components that
 * gate on the same flag (e.g. the Lab Insights "New" badge shown in two places)
 * stay in sync.
 *
 * All keys use the `persist_` prefix so they survive logout — see
 * `clearStoragePreservingTours` in `@/utils/storage-utils`. Without it,
 * dismissed promos/badges would reappear after the user logs back in.
 */
export const DISMISS_KEYS = {
  dashboardFeatureCards: "persist_dashboardFeatureCardsDismissed",
  /** @deprecated use labInsightsNewBadgeFirstSeen + useTimeGatedBadge instead */
  labInsightsNewBadge: "persist_labInsightsNewBadgeSeen",
  labInsightsNewBadgeFirstSeen: "persist_labInsightsNewBadgeFirstSeen",
  labInsightsBanner: "persist_labInsightsBannerDismissed",
} as const;

/**
 * Persists a one-time dismissal flag in localStorage.
 *
 * `dismissed` is true once `dismiss()` has been called — now or in any previous
 * session. Use for dismissible promos and "New" badges that should never return
 * after the first interaction.
 */
export const useDismissible = (storageKey: string) => {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey) === "true";
    } catch {
      // localStorage can throw in private mode — treat as not dismissed
      return false;
    }
  });

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(storageKey, "true");
    } catch {
      // Persisting failed (private mode/quota); still hide for this session
    }
    setDismissed(true);
  }, [storageKey]);

  return { dismissed, dismiss };
};

/**
 * Time-gated badge visibility. Stores a "first seen" timestamp on first call;
 * returns `visible = true` until `days` have elapsed from that timestamp, then
 * `false` permanently. No manual dismiss — the badge retires itself after the
 * specified window. Survives logout (uses the `persist_` prefix convention).
 */
export const useTimeGatedBadge = (storageKey: string, days: number) => {
  const visible = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) {
        localStorage.setItem(storageKey, Date.now().toString());
        return true;
      }
      const firstSeenMs = parseInt(stored, 10);
      if (isNaN(firstSeenMs)) return false;
      return Date.now() - firstSeenMs < days * 24 * 60 * 60 * 1000;
    } catch {
      return false;
    }
  })[0];

  return { visible };
};

/**
 * Like {@link useDismissible} but tracks a set of independently dismissible
 * items under one key (stored as a JSON array). Used when a group of items
 * (e.g. two promo cards) can each be dismissed on their own.
 */
export const useDismissibleSet = (storageKey: string) => {
  const [dismissed, setDismissed] = useState<string[]>(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(storageKey) ?? "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const dismiss = useCallback(
    (key: string) => {
      setDismissed((prev) => {
        if (prev.includes(key)) return prev;
        const next = [...prev, key];
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          // Persisting failed (private mode/quota); still hide for this session
        }
        return next;
      });
    },
    [storageKey]
  );

  return { dismissed, dismiss };
};
